import classNames from "classnames";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router";
import { SaveIcon, TrashIcon } from "../../../../../../assets/icons";
import { Doctrine } from "../../../../../models";
import {
  getLoggedUser,
  isViewedItem,
  saveViewedItem,
} from "../../../../../utils";
import {
  formatDate,
  getText,
  sanitizeHtml,
} from "../../../../../utils/_generic";
import {
  getHighlightedExcerpts,
  highlightFullText,
} from "../../../../../utils/_getHighlightedExcerpts";
import { saveDocumentWithNotify } from "../../../../../utils/_handleSaveDocument";
import PDLTypography from "../../../dna/_pdltypography";
import { ActionButton } from "../../../global/_actionButton";

export interface DouctrineItemProps {
  item: Doctrine;
  index: number;
  searchTerm?: string;
  pushDown?: boolean;
  cardProfileStyle?: boolean;
  onRemove?: (item: Doctrine) => void;
}

const DouctrineItem = ({
  item,
  index,
  searchTerm,
  pushDown = false,
  cardProfileStyle = false,
  onRemove,
}: DouctrineItemProps) => {
  const dispatch = useDispatch();

  const [isSavingDocument, setIsSavingDocument] = useState(false);
  const [showAllAuthors, setShowAllAuthors] = useState(false);

  const loggedUser = useSelector(getLoggedUser);

  const isIpAccount = loggedUser.origem === "ip_auth";

  const itemId = item.id || `generic-${index}`;
  const sanitizedHtml = sanitizeHtml(item.texto || "");
  const textOnly = getText(sanitizedHtml);

  const excerpt = searchTerm
    ? getHighlightedExcerpts(textOnly, searchTerm)
    : null;

  const highlightedTitle = highlightFullText(item.titulo || "", searchTerm);

  const displayTitle = highlightedTitle ? highlightedTitle : item.titulo || "";

  const cleanTitle = sanitizeHtml(displayTitle);

  const opened = isViewedItem(itemId) && !cardProfileStyle;

  const handleSaveDocument = () =>
    saveDocumentWithNotify({
      dispatch,
      setIsSavingDocument,
      documentoId: item?._id,
      id: item?.id,
      tipo: "Doutrina",
      url: window.location.href,
    });

  const renderActions = (className: string) => {
    if (!isIpAccount)
      return (
        <div
          className={classNames("btn-action-container flex gap-2", className)}
        >
          <ActionButton
            icon={<SaveIcon />}
            onClick={handleSaveDocument}
            disabled={isSavingDocument}
            label={isSavingDocument ? "Salvando..." : "Salvar"}
          />
        </div>
      );
  };

  return (
    <div
      key={itemId}
      id={`result-item-${itemId}`}
      className={`pdl_components_lista_resultados_item mt-3 ${
        opened ? "opened" : ""
      } ${pushDown ? "push-down" : ""} ${cardProfileStyle ? "card-style" : ""}`}
    >
      {renderActions("mobile")}
      <header className="title-wrapper">
        <Link
          to={`/doutrinas/${item.id}`}
          className="text-decoration-none"
          onClick={() => {
            saveViewedItem(itemId);
            sessionStorage.setItem("last-clicked-result-item", itemId);
          }}
        >
          <PDLTypography
            size="md"
            weight="semibold"
            variant="brand"
            upper
            className="title-link"
          >
            <span
              className="pdl-title-clamp-3 item-title"
              dangerouslySetInnerHTML={{
                __html: `${
                  opened ? `<span class="opened-status">Acessado</span>` : ""
                } ${cleanTitle}`,
              }}
            />
          </PDLTypography>
        </Link>
      </header>

      <div className="result-meta mb-2 mt-2 flex justify-between items-start">
        <div className="flex">
          <PDLTypography as="span" size="sm">
            Doutrina
          </PDLTypography>
          {item.autores && (
            <>
              &nbsp;&bull;&nbsp;
              <PDLTypography as="span" size="sm">
                {showAllAuthors || item.autores.length === 1 ? (
                  item.autores.join(", ")
                ) : (
                  <>
                    {item.autores[0]}{" "}
                    <span
                      onClick={() => setShowAllAuthors(!showAllAuthors)}
                      className="show_more_authors_button "
                      title="Clique para ver todos os autores"
                    >
                      +{item.autores.length - 1}
                    </span>
                  </>
                )}
              </PDLTypography>
            </>
          )}
          {item.areas?.length > 0 && (
            <>
              &nbsp;&bull;&nbsp;
              <PDLTypography as="span" size="sm">
                {item.areas.join(", ")}
              </PDLTypography>
            </>
          )}
          {item.data && (
            <>
              &nbsp;&bull;&nbsp;
              <PDLTypography as="span" size="sm">
                {formatDate(item.data)}
              </PDLTypography>
            </>
          )}
        </div>
        {renderActions("desktop")}
      </div>

      <div className="text-container">
        {item.resumo && (
          <div className="mb-2">
            <PDLTypography size="sm" weight="medium" className="mb-1">
              Resumo:
            </PDLTypography>
            <p className="content text-sm text-gray-800">{item.resumo}</p>
          </div>
        )}
      </div>

      <div className="text-container">
        {!!excerpt && (
          <PDLTypography size="sm" weight="medium" className="mb-1">
            Texto encontrado em:
          </PDLTypography>
        )}

        <div
          className="content text-sm text-gray-800"
          dangerouslySetInnerHTML={{
            __html: excerpt || textOnly.slice(0, 500),
          }}
        />
      </div>

      {pushDown && (
        <div
          className="btn-action-container-footer"
          style={{ justifyContent: "flex-end" }}
        >
          {onRemove && (
            <div className="btn-action-group-right">
              <button
                type="button"
                className="btn-remove-saved"
                onClick={() => onRemove(item)}
              >
                <TrashIcon color="#8c0000" />
                <span>Remover de salvos</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DouctrineItem;
